import { useState, useEffect } from "react";
import { Lead } from "./lead-card";
import { format, isToday, isYesterday, isThisWeek, isThisMonth } from "date-fns";
import { de } from "date-fns/locale";
import { ChevronDown, ChevronUp, Calendar, Clock, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { getUserName } from "@/services/userService";

interface LeadTimelineProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
}

// Helper function to group leads by date for the timeline
const groupLeadsByDate = (leads: Lead[]): Record<string, Lead[]> => {
  const groupedLeads: Record<string, Lead[]> = {};
  
  leads.forEach(lead => {
    // Use lastContactDate if available, otherwise use nextContactDate
    let dateToUse = lead.lastContactDate || lead.nextContactDate;
    if (!dateToUse) {
      // If no dates available, group under "No date"
      const key = "No date";
      if (!groupedLeads[key]) {
        groupedLeads[key] = [];
      }
      groupedLeads[key].push(lead);
      return;
    }
    
    try {
      const date = new Date(dateToUse);
      const key = format(date, "yyyy-MM-dd");
      
      if (!groupedLeads[key]) {
        groupedLeads[key] = [];
      }
      groupedLeads[key].push(lead);
    } catch (error) {
      console.error("Error parsing date:", error, dateToUse);
      // If date parsing fails, group under "Invalid date"
      const key = "Invalid date";
      if (!groupedLeads[key]) {
        groupedLeads[key] = [];
      }
      groupedLeads[key].push(lead);
    }
  });
  
  return groupedLeads;
};

// Helper to format the date for display
const formatDateForDisplay = (dateString: string): string => {
  if (dateString === "No date" || dateString === "Invalid date") {
    return dateString;
  }
  
  try {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return "Heute";
    } else if (isYesterday(date)) {
      return "Gestern";
    } else if (isThisWeek(date)) {
      return format(date, "'Diese Woche,' EEEE", { locale: de });
    } else if (isThisMonth(date)) {
      return format(date, "'Diesen Monat,' d. MMMM", { locale: de });
    } else {
      return format(date, "d. MMMM yyyy", { locale: de });
    }
  } catch (error) {
    console.error("Error formatting date for display:", error, dateString);
    return dateString;
  }
};

// Format status for display
const formatStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    "new": "Neu",
    "old-contact": "Altkontakt",
    "not-reached": "Nicht erreicht",
    "follow-up": "Wiedervorlage",
    "scheduled": "Terminiert",
    "in-progress": "In Prozess",
    "won": "Gewonnen",
    "lost": "Verloren",
    "wrong-data": "Falsche Daten",
    "delete-data": "Daten löschen"
  };
  
  return statusMap[status] || status;
};

// Format time for display
const formatTime = (dateString: string | Date | undefined): string => {
  if (!dateString) return "";
  
  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    return format(date, "HH:mm", { locale: de });
  } catch (error) {
    console.error("Error formatting time:", error, dateString);
    return "";
  }
};

// Status color mapping
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    "new": "bg-blue-500",
    "in-progress": "bg-amber-500",
    "callback": "bg-purple-500",
    "offer": "bg-emerald-500",
    "negotiation": "bg-orange-500",
    "won": "bg-green-500",
    "lost": "bg-red-500",
    "paused": "bg-gray-500"
  };
  
  return colorMap[status] || "bg-gray-400";
};

// Get priority badge variant
const getPriorityBadge = (priority: string): string => {
  const priorityMap: Record<string, string> = {
    "low": "bg-gray-100 text-gray-800 border-gray-200",
    "medium": "bg-amber-100 text-amber-800 border-amber-200",
    "high": "bg-red-100 text-red-800 border-red-200"
  };
  
  return priorityMap[priority] || "bg-gray-100 text-gray-800 border-gray-200";
};

// Hilfskomponente für einzelne Lead-Einträge in der Timeline, mit Benutzeranzeige
function LeadTimelineEntry({ lead, onClick }: { lead: Lead, onClick: (lead: Lead) => void }) {
  const [assignedUserName, setAssignedUserName] = useState<string>("");
  const dateToDisplay = lead.lastContactDate || lead.nextContactDate;
  
  useEffect(() => {
    const loadUserName = async () => {
      if (lead.assignedTo) {
        const name = await getUserName(lead.assignedTo);
        setAssignedUserName(name);
      }
    };
    
    loadUserName();
  }, [lead.assignedTo]);
  
  return (
    <div 
      className="relative mb-4"
    >
      {/* Timeline circle */}
      <div className={`absolute left-[-8px] top-6 w-4 h-4 rounded-full ${getStatusColor(lead.status)} border-2 border-background z-10`} />
      
      <div 
        className="ml-6 p-4 bg-card rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => onClick(lead)}
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="font-medium text-base">{lead.name}</h3>
            <p className="text-sm text-muted-foreground">{lead.company}</p>
          </div>
          
          {dateToDisplay && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5 mr-1" />
              {formatTime(dateToDisplay)}
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2 mb-2">
          <Badge variant="secondary">
            {formatStatus(lead.status)}
          </Badge>
          
          <Badge variant="outline" className={getPriorityBadge(lead.priority)}>
            {lead.priority === "low" ? "Niedrig" : 
              lead.priority === "medium" ? "Mittel" : "Hoch"}
          </Badge>
          
          <div className="flex items-center">
            <User className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
            <span className="text-xs text-gray-600">{assignedUserName || "..."}</span>
          </div>
        </div>
        
        {lead.email && (
          <p className="text-sm mb-1">
            <span className="font-medium">E-Mail:</span> {lead.email}
          </p>
        )}
        
        {lead.phone && (
          <p className="text-sm mb-1">
            <span className="font-medium">Telefon:</span> {lead.phone}
          </p>
        )}
        
        {lead.notes && lead.notes.length > 100 ? (
          <p className="text-sm mt-2 text-muted-foreground">
            {lead.notes.substring(0, 100)}...
          </p>
        ) : lead.notes ? (
          <p className="text-sm mt-2 text-muted-foreground">{lead.notes}</p>
        ) : null}
        
        {lead.history && lead.history.length > 0 && (
          <div className="mt-3 pt-2 border-t text-sm">
            <p className="font-medium text-xs text-muted-foreground mb-1">
              Letzte Aktivität: {new Date(lead.history[0].date).toLocaleDateString('de-DE')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function LeadTimeline({ leads, onLeadClick }: LeadTimelineProps) {
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
  
  // Group leads by date
  const groupedLeads = groupLeadsByDate(leads);
  
  // Sort date keys (newest first)
  const sortedDateKeys = Object.keys(groupedLeads).sort((a, b) => {
    // Special case for "No date" and "Invalid date"
    if (a === "No date" || a === "Invalid date") return 1;
    if (b === "No date" || b === "Invalid date") return -1;
    
    // Otherwise sort by date (newest first)
    return new Date(b).getTime() - new Date(a).getTime();
  });
  
  const toggleDateExpansion = (dateKey: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };
  
  // Initialize all dates as expanded by default
  if (sortedDateKeys.length > 0 && Object.keys(expandedDates).length === 0) {
    const initialExpandedState: Record<string, boolean> = {};
    sortedDateKeys.forEach(dateKey => {
      initialExpandedState[dateKey] = true;
    });
    setExpandedDates(initialExpandedState);
  }
  
  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <p className="text-muted-foreground">Keine Leads gefunden</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-full pr-4">
      <div className="flex flex-col">
        {sortedDateKeys.map(dateKey => (
          <div key={dateKey} className="mb-6">
            <Collapsible 
              open={expandedDates[dateKey]} 
              onOpenChange={() => toggleDateExpansion(dateKey)}
            >
              <div className="flex items-center mb-2">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center text-lg font-medium p-2 hover:bg-muted/50 rounded-md">
                    {expandedDates[dateKey] ? (
                      <ChevronUp className="h-5 w-5 mr-2 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 mr-2 text-muted-foreground" />
                    )}
                    <Calendar className="h-5 w-5 mr-2 text-primary" />
                    <span>{formatDateForDisplay(dateKey)}</span>
                    <Badge variant="outline" className="ml-2">
                      {groupedLeads[dateKey].length}
                    </Badge>
                  </button>
                </CollapsibleTrigger>
              </div>
              
              <CollapsibleContent>
                <div className="relative pl-8 ml-4">
                  {/* Vertical timeline line */}
                  <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-border" />
                  
                  {groupedLeads[dateKey].map((lead, index) => (
                    <LeadTimelineEntry key={lead.id} lead={lead} onClick={onLeadClick} />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
